import {
  chunk,
  compact,
  difference,
  flatMap,
  isEmpty,
  map,
  reduce,
  size,
  toUpper,
} from "lodash";
import { DateMath } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { tr } from "@radarly/i18n";
import { Card } from "@radarly/models/cards";
import { AICardFactory } from "@radarly/models/cards/types/ai";
import { ClassificationCardFactory } from "@radarly/models/cards/types/classification";
import { ClusterCardFactory } from "@radarly/models/cards/types/cluster";
import { DateMetricCardFactory } from "@radarly/models/cards/types/date.metric";
import { DateTermCardFactory } from "@radarly/models/cards/types/date.term";
import { DemographicCardFactory } from "@radarly/models/cards/types/demographic";
import { GeoCardFactory } from "@radarly/models/cards/types/geo";
import { PivotCardFactory } from "@radarly/models/cards/types/pivot";
import { PostsCardFactory } from "@radarly/models/cards/types/posts";
import { TermCardFactory } from "@radarly/models/cards/types/term";
import { TermMetricsCardFactory } from "@radarly/models/cards/types/term.metrics";
import { TimeUnit } from "@radarly/models/cards/types/time-unit";
import { UsersCardFactory } from "@radarly/models/cards/types/users";
import { Clause } from "@radarly/models/clause";
import { createClauseFromInnerClause } from "@radarly/models/clause/creator";
import { mergeClauses } from "@radarly/models/clause/merge";
import { getAccessDefault } from "@radarly/models/insight-pages/access";
import { getRandomInsightPageColor } from "@radarly/models/insight-pages/creator";
import {
  CardRow,
  Layout,
  Row,
  TitleRow,
} from "@radarly/models/insight-pages/model";
import {
  isWizardAICard,
  isWizardClassificationCard,
  isWizardClusterCard,
  isWizardDateMetricCard,
  isWizardDateTermCard,
  isWizardDemographicCard,
  isWizardGeoCard,
  isWizardPivotCard,
  isWizardPostsCard,
  isWizardTermCard,
  isWizardTermMetricsCard,
  isWizardUsersCard,
} from "./card";
import {
  hasMatchingWithoutConditionSection,
  hasValidConfigurationSection,
} from "./configuration";
import { WizardTemplate, WizardTemplateSection } from "./model";
import { WizardCard } from "./model/card";
import {
  WizardConfiguration,
  WizardConfigurationClauseField,
  WizardConfigurationLoopField,
} from "./model/configuration";
import {
  CardsSection,
  LoopCardsSection,
  WizardCardRow,
  WizardConfigurationConditionField,
  WizardTitleRow,
} from "./model/section";
import {
  getMandatoryFieldsFromSections,
  isDescriptionSection,
  isLoopCardsSection,
  isWizardCardRow,
} from "./section";

function labelOrTr(locale: Locale, key: string) {
  const upperKey = toUpper(key);
  const translatedLabel = tr(locale, key);
  if (upperKey === translatedLabel) {
    return key;
  } else {
    return translatedLabel;
  }
}

type InsightPageGenerationOptions = Partial<{
  title: string;
  color: string;
}>;

type LoopItems = Partial<
  Record<
    WizardConfigurationLoopField,
    {
      id: string | number;
      label: string;
    }[]
  >
>;

class WizardGenerator {
  private projectId: number;
  private projectFocuses: number[];
  private workspaceId: number;
  private authorId: number;
  private locale: Locale;
  private timezone: Timezone;
  private description: string;
  private aiContext: string;
  private personIdentifications: number[];
  private includeAIAnalysis: boolean;
  private clauses: Partial<
    Record<keyof WizardConfiguration["fields"], Clause>
  > = {};
  private loop: LoopItems = {};
  private timeUnit: TimeUnit;
  private period: {
    from: string | DateMath;
    to: string | DateMath;
  };
  public setProjectId(projectId: number) {
    this.projectId = projectId;
    return this;
  }
  public setProjectFocuses(projectFocuses: number[]) {
    this.projectFocuses = projectFocuses;
    return this;
  }
  public setWorkspaceId(workspaceId: number) {
    this.workspaceId = workspaceId;
    return this;
  }
  public setAuthorId(authorId: number) {
    this.authorId = authorId;
    return this;
  }
  public setLocale(locale: Locale) {
    this.locale = locale;
    return this;
  }
  public setTimezone(timezone: Timezone) {
    this.timezone = timezone;
    return this;
  }
  public setDescription(description: string) {
    this.description = description;
    return this;
  }
  public setAIContext(aiContext: string) {
    this.aiContext = aiContext;
    return this;
  }
  public setIncludeAIAnalysis(includeAINegativeAnalysis: boolean) {
    this.includeAIAnalysis = includeAINegativeAnalysis;
    return this;
  }
  public setPersonIdentifications(personIdentifications: number[]) {
    this.personIdentifications = personIdentifications;
    return this;
  }
  private generatePersonIdentificationsClause() {
    // Special use case for personIdentification: since it's not configurable but coming from the project spec
    if (this?.personIdentifications && this?.personIdentifications.length > 0) {
      this.clauses.personIdentifications = {
        and: [
          {
            imageRecognitionPersonIdentifications: [
              ...this.personIdentifications,
            ],
          },
        ],
      };
    }
  }

  public setClauses(
    clauses: Partial<Record<keyof WizardConfiguration["fields"], Clause>>
  ) {
    this.clauses = clauses;
    this.generatePersonIdentificationsClause();
  }
  public setLoopItems(loop: LoopItems) {
    this.loop = loop;
  }
  public setPeriod(period: { from: string | DateMath; to: string | DateMath }) {
    this.period = period;
  }
  public generateInsightPage(
    template: WizardTemplate,
    options: Partial<WizardConfiguration>
  ) {
    if (
      !this.hasValidConfiguration() ||
      !this.authorId ||
      isEmpty(this.timezone)
    ) {
      throw new Error("Invalid configuration");
    }
    return {
      ...this.getNewInsightPage(template, options),
      rows: this.generateRowsFromWizardTemplate(template, options),
    };
  }
  private hasValidConfiguration() {
    if (!this.projectId || !this.workspaceId) {
      return false;
    }
    return true;
  }
  private generateRowsFromWizardTemplate(
    template: WizardTemplate,
    options: Partial<WizardConfiguration>
  ): Row[] {
    return flatMap(
      compact(
        map(template.sections, (section) =>
          this.generateInsightPageRowsFromSection(section, options)
        )
      )
    );
  }
  public generateInsightPageRowsFromSection(
    section: WizardTemplateSection,
    options: Partial<WizardConfiguration>,
    isPreview: boolean = false
  ): Row[] {
    if (isDescriptionSection(section)) {
      if (isEmpty(this.description)) {
        return [];
      }
      return [this.getDescriptionRow()];
    } else {
      if (hasMatchingWithoutConditionSection(section, options.fields)) {
        return [];
      } else if (!hasValidConfigurationSection(section, options.fields)) {
        return isPreview && !section.conditions
          ? [
              {
                type: "placeholder",
                field: difference(getMandatoryFieldsFromSections([section]), [
                  "query",
                  "queries",
                ])[0],
              },
            ]
          : [];
      } else if (isLoopCardsSection(section)) {
        if (size(this.getLoopItems(section)) > 3) {
          return [];
        }

        const filtredSection = this.filterCardSection(section);
        return this.generateRowsFromLoopCardSection(filtredSection);
      } else {
        const filtredSection = this.filterCardSection(section);
        return this.generateRowsFromCardSection(filtredSection);
      }
    }
  }

  private filterCardSectionForAICards<
    T extends CardsSection | LoopCardsSection
  >(section: T) {
    return {
      ...section,
      rows: (section.rows as any).filter(
        (e) =>
          this.includeAIAnalysis ||
          (e.type !== "ai" && e.type !== "card" && e.type !== "cards") ||
          ((e.type === "cards" || e.type === "card") &&
            !e.cards.some((card) => isWizardAICard(card)))
      ) as T["rows"],
    };
  }

  private filterForPersonIdentifications<
    T extends CardsSection | LoopCardsSection
  >(section: T) {
    if (section?.clause && section.clause.includes("personIdentifications")) {
      return this.clauses?.personIdentifications
        ? section
        : { ...section, rows: [] };
    }
    return section;
  }

  private filterCardSection(section: CardsSection): CardsSection;
  private filterCardSection(section: LoopCardsSection): LoopCardsSection;
  private filterCardSection<T extends CardsSection | LoopCardsSection>(
    section: T
  ) {
    const sectionFiltredForAI = this.filterCardSectionForAICards(section);
    return this.filterForPersonIdentifications(sectionFiltredForAI);
  }

  private generateRowsFromCardSection(section: CardsSection): Row[] {
    return map(section.rows, (row) => {
      if (isWizardCardRow(row)) {
        return this.generateRowFromCardRow(section, row);
      } else {
        return this.generateTitleRowFromCardRow(row);
      }
    });
  }

  private getClauseArray(
    clauses: WizardConfigurationConditionField
  ): WizardConfigurationClauseField[] {
    return reduce(
      clauses.without,
      (acc, withoutField) => {
        //@ts-ignore
        acc.push("without_" + withoutField);
        return acc;
      },
      clauses.with || []
    );
  }
  private getCardClause(
    card: WizardCard,
    section: CardsSection | LoopCardsSection
  ) {
    return reduce(
      card.wizardClause
        ? this.getClauseArray(card.wizardClause)
        : section.clause,
      (clause, field) => {
        if (isEmpty(this.clauses[field])) {
          return clause;
        }
        return mergeClauses(clause, this.clauses[field]);
      },
      card.clause
    );
  }
  private generateRowFromCardRow(section: CardsSection, row: WizardCardRow) {
    return {
      type: "card" as const,
      layout: row.layout,
      cards: map(row.cards, (card) => {
        const ipCard = this.generateCardFromWizardCard(
          card,
          this.getCardClause(card, section)
        );
        return {
          ...ipCard,
          dataviz: card.dataviz,
        };
      }),
    } as CardRow;
  }
  private generateTitleRowFromCardRow(row: WizardTitleRow) {
    return {
      ...(row as TitleRow),
      titles: map(row.titles, (title) => labelOrTr(this.locale, title)),
    };
  }
  private generateCardFromWizardCard(card: WizardCard, clause: Clause): Card {
    const title = labelOrTr(this.locale, card.title);
    const from = this.period?.from;
    const to = this.period?.to;
    const timeUnit = this.timeUnit;
    if (isWizardDateMetricCard(card)) {
      return DateMetricCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          metric: card.metric,
          clause,
          from,
          to,
          timeUnit,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardPostsCard(card)) {
      return PostsCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          sortBy: card.sort.on,
          clause,
          from,
          to,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardTermCard(card)) {
      return {
        ...TermCardFactory.getInstance().fromInput(
          this.workspaceId,
          this.projectFocuses,
          title,
          {
            metric: card.metric,
            term: card.term,
            clause,
            from,
            to,
            fullDataviz: card.dataviz,
          }
        ),
        dataviz: card.dataviz,
      };
    } else if (isWizardUsersCard(card)) {
      return UsersCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          sortBy: card.sort,
          clause,
          from,
          to,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardGeoCard(card)) {
      return GeoCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          metric: card.metric,
          term: card.term,
          clause,
          from,
          to,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardDemographicCard(card)) {
      return DemographicCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          metric: card.metric,
          clause,
          from,
          to,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardDateTermCard(card)) {
      return DateTermCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          metric: card.metric,
          term: card.term,
          clause,
          from,
          to,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardTermMetricsCard(card)) {
      return TermMetricsCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          metrics: card.metrics,
          term: card.term,
          clause,
          from,
          to,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardClassificationCard(card)) {
      return ClassificationCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          metric: card.metric,
          clause,
          from,
          to,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardAICard(card)) {
      return AICardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          type: card.analysis.type,
          includeNegativeAnalysis: false,
          useInsightPageAIContext: true,
          clause,
          from,
          to,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardClusterCard(card)) {
      return ClusterCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          clause,
          from,
          to,
          fullDataviz: card.dataviz,
        }
      );
    } else if (isWizardPivotCard(card)) {
      return PivotCardFactory.getInstance().fromInput(
        this.workspaceId,
        this.projectFocuses,
        title,
        {
          clause,
          from,
          to,
          metric: card.metric,
          term: card.term,
          pivot: card.pivot,
          fullDataviz: card.dataviz,
        }
      );
    } else {
      throw new Error();
    }
  }

  private generateRowsFromLoopCardSection(section: LoopCardsSection): Row[] {
    return reduce(
      section.rows,
      (templateRows, row) => {
        if (row.type === "title") {
          return [...templateRows, this.generateTitleRowFromCardRow(row)];
        }
        const allQueries = this.getLoopItems(section);
        const newRows = reduce(
          chunk(allQueries, 3),
          (rows, chunk) => [
            ...rows,
            this.generateRowFromLoopCard(section, chunk, row),
          ],
          []
        );
        return [...templateRows, ...newRows];
      },
      []
    );
  }

  private getRowLayout(rowQueriesLength: number) {
    if (rowQueriesLength === 3) return Layout.Row_3;
    if (rowQueriesLength === 2) return Layout.Row_2;
    return Layout.Row_1;
  }

  private generateRowFromLoopCard(
    section: LoopCardsSection,
    items,
    card: WizardCard
  ) {
    return {
      type: "card" as const,
      layout: this.getRowLayout(size(items)),
      cards: map(items, (item) => {
        const ipCard = this.generateCardFromWizardCard(
          {
            ...card,
            title: card.title === "::LOOP_ITEM::" ? item?.label : card.title,
          },
          mergeClauses(this.getCardClause(card, section), item.clause)
        );
        return {
          ...ipCard,
          dataviz: card.dataviz,
        };
      }),
    } as CardRow;
  }

  private getLoopItems(section: LoopCardsSection) {
    if (section.loop === "queries") {
      return map(this.loop.queries, (query) => ({
        id: query.id,
        label: query.label,
        clause: createClauseFromInnerClause({
          [Facet.Focuses]: [query.id],
        }),
      }));
    } else {
      return [];
    }
  }
  private getDescriptionRow() {
    return {
      layout: Layout.Row_1,
      type: "title" as const,
      style: "text" as const,
      titles: [this.description],
    };
  }
  private getNewInsightPage(
    template: WizardTemplate,
    options?: InsightPageGenerationOptions
  ) {
    return {
      id: "",
      title:
        options?.title ||
        tr(
          this.locale,
          "INSIGHT_PAGE_TEMPLATE_" + template.id + "::CONFIGURATION_TITLE_LABEL"
        ),
      aiContext: this.aiContext,
      projectId: this.projectId,
      dashboardId: this.workspaceId,
      projectFocuses: this.projectFocuses,
      author: this.authorId,
      timezone: this.timezone,
      access: getAccessDefault(),
      rows: [],
      picture: "",
      description: "",
      color: options?.color || getRandomInsightPageColor(),
      filters: {
        static: [],
        dynamic: true,
      },
      periodSelector: true,
      sharedWith: [],
    };
  }
}

export class WizardGeneratorFactory {
  static getInstance() {
    return new WizardGenerator();
  }
}
